/**
 * Loads the complete cup/continental competition taxonomy for player filters.
 * @module hooks/useCompetitionOptions
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  usePlayerSearchListCompetitionsQuery,
  type PlayerSearchListCompetitionsQuery,
} from "@/generated/graphql";
import type { FilterOption } from "@/types/filter";

/** Large enough for today's taxonomy while pagination still handles growth. */
const COMPETITION_PAGE_SIZE = 100;

/** Select option backed by the generated Competition query. */
export interface CompetitionFilterOption extends FilterOption {
  readonly code?: string | null;
}

/** Result returned by the competition option hook. */
interface CompetitionOptionsResult {
  readonly competitionOptions: readonly CompetitionFilterOption[];
  readonly loading: boolean;
  readonly error: Error | undefined;
  readonly retry: () => Promise<void>;
}

/**
 * Appends a newly fetched page without duplicating competition IDs.
 * @param previous - Previously cached competition connection
 * @param next - Newly fetched connection page
 * @returns Merged connection with the newest pageInfo
 */
const mergeCompetitionPages = (
  previous: PlayerSearchListCompetitionsQuery,
  next: PlayerSearchListCompetitionsQuery
): PlayerSearchListCompetitionsQuery => {
  const seen = new Set(
    previous.listCompetitions.edges.map(edge => edge.node.id)
  );
  return {
    ...next,
    listCompetitions: {
      ...next.listCompetitions,
      edges: [
        ...previous.listCompetitions.edges,
        ...next.listCompetitions.edges.filter(edge => !seen.has(edge.node.id)),
      ],
    },
  };
};

/**
 * Fetches every `listCompetitions` page and exposes an alphabetical option list.
 * @param options - Query options
 * @param options.skip - Whether authentication is unavailable
 * @returns Alphabetical competition options and aggregate loading state
 */
const useCompetitionOptions = (options?: {
  readonly skip?: boolean;
}): CompetitionOptionsResult => {
  const requestedCursors = useRef(new Set<string>());
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [paginationError, setPaginationError] = useState<Error>();
  const {
    data,
    loading,
    error: queryError,
    fetchMore,
    refetch,
  } = usePlayerSearchListCompetitionsQuery({
    variables: { first: COMPETITION_PAGE_SIZE },
    skip: options?.skip,
    fetchPolicy: "cache-first",
  });

  const hasNextPage = data?.listCompetitions.pageInfo.hasNextPage ?? false;
  const endCursor = data?.listCompetitions.pageInfo.endCursor;

  const competitionOptions = useMemo(
    (): readonly CompetitionFilterOption[] =>
      [...(data?.listCompetitions.edges ?? [])]
        .map(edge => ({
          id: edge.node.id,
          label: edge.node.name,
          code: edge.node.code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data?.listCompetitions.edges]
  );

  const requestPage = useCallback(
    async (cursor: string): Promise<void> => {
      if (requestedCursors.current.has(cursor)) return;
      requestedCursors.current.add(cursor);
      setPaginationLoading(true);
      setPaginationError(undefined);
      try {
        await fetchMore({
          variables: { first: COMPETITION_PAGE_SIZE, after: cursor },
          updateQuery: (previous, { fetchMoreResult }) =>
            fetchMoreResult
              ? mergeCompetitionPages(previous, fetchMoreResult)
              : previous,
        });
      } catch (cause: unknown) {
        requestedCursors.current.delete(cursor);
        setPaginationError(
          cause instanceof Error
            ? cause
            : new Error("Competition options could not be loaded")
        );
      } finally {
        setPaginationLoading(false);
      }
    },
    [fetchMore]
  );

  const retry = useCallback(async (): Promise<void> => {
    if (paginationError && endCursor) {
      await requestPage(endCursor);
      return;
    }
    await refetch();
  }, [endCursor, paginationError, refetch, requestPage]);

  useEffect(() => {
    if (!hasNextPage || !endCursor) return;
    void Promise.resolve().then(() => requestPage(endCursor));
  }, [endCursor, hasNextPage, requestPage]);

  return {
    competitionOptions,
    loading: loading || paginationLoading,
    error: paginationError ?? queryError,
    retry,
  };
};

export default useCompetitionOptions;
